<!-- The tab shell. THIS FILE IS YOURS.

     IonTabs wraps its OWN IonRouterOutlet, and that is what gives each tab a
     separate navigation stack: pushing /tabs/items/3 leaves the tab bar in
     place with a working back button, and switching away and back returns you
     to the detail screen rather than the top of the list. A single top-level
     outlet with a tab bar beside it cannot do that — it has one stack, so
     every tab switch is a replace and every push loses the bar.

     TWO PROPS ON EVERY TAB BUTTON, AND BOTH ARE LOAD-BEARING:
       `tab`   the id IonTabs tracks it by
       `href`  what IonTabs matches against the current route to decide which
               button is active

     The href is NOT followed as a link — IonTabButton calls
     navManager.changeTab(), which pushes through the router — so it works
     under the memory history this app uses. A button without an href is a tab
     that navigates fine and never highlights, which is a confusing five
     minutes to debug because nothing errors.

     THE IonPage WRAPPER IS REQUIRED. IonTabs is not itself a page, so a shell
     whose root is IonTabs gives the PARENT outlet nothing with `.ion-page` to
     transition, and Ionic warns "the view you are trying to render does not
     have the required <ion-page> component" on every tab change. It renders
     anyway, which is why this is easy to leave broken: what you lose is the
     enter/leave animation and the page lifecycle events. -->
<template>
  <IonPage>
    <IonTabs>
      <IonRouterOutlet />

      <IonTabBar slot="bottom">
        <!-- A FLAT v-for, NOT a <template v-for>. IonTabs reads its slot
             children directly to build its tab bookkeeping and does not unwrap
             fragments, so wrapping these makes every child a Fragment vnode and
             the whole thing throws `Cannot read properties of undefined`. -->
        <IonTabButton
          v-for="t in TABS"
          :key="t.name"
          :tab="t.name"
          :href="t.path"
        >
          <IonIcon :icon="t.icon" aria-hidden="true" />
          <IonLabel>{{ t.label }}</IonLabel>
        </IonTabButton>

      </IonTabBar>
    </IonTabs>
  </IonPage>
</template>

<script setup>
import {
  IonPage, IonTabs, IonRouterOutlet, IonTabBar, IonTabButton, IonIcon, IonLabel,
} from '@ionic/vue';
import { TABS } from '../router.js';
</script>

<style>
/* THE SHELL DECLARES HOW MUCH OF THE BOTTOM IT HAS SPENT.
 *
 * An `ion-toast` is presented from the app root and knows nothing about this
 * bar, so without a number the install prompt and every undo bar land squarely
 * on top of the app's primary navigation — the tab buttons cannot be tapped
 * while a toast is over them.
 *
 * `tokens.css` declares `--tabbar-clearance: 0px` and `ionic.css` spends it on
 * `ion-toast`. This is the only file that knows the bar exists, so this is
 * where the value belongs: a shell with no bottom bar (stack, sidebar) leaves
 * it at zero and the rule costs nothing.
 *
 * 56px is Ionic's default tab-bar height. The safe-area inset is NOT included —
 * `ionic.css` adds it separately, because on a home-indicator phone the bar is
 * its buttons plus the inset below them.
 *
 * Unscoped deliberately: `--tabbar-clearance` has to reach `ion-toast`, which
 * is presented at the app root and is not a descendant of this component. */
:root {
  --tabbar-clearance: 56px;
}
</style>
